import { ChessTermType, TermAction, MoveType, } from "@chesshotel/types";
import { Chess, swapColor } from "@chesshotel/chess";
import { exchangeEvaluation, uci2move, pieceValues, moveEquals, getActionTypes, } from "./util.js";
import { detectSacrifice } from "./sacrifice.js";
const SACRIFICE_CACHE_CAP = 5000;
const sacrificeCache = new Map();
function cachedDetectSacrifice(fen, move) {
    const key = `${fen}|${move}`;
    const cached = sacrificeCache.get(key);
    if (cached !== undefined)
        return cached;
    const result = detectSacrifice(fen, move) ?? null;
    if (sacrificeCache.size > SACRIFICE_CACHE_CAP)
        sacrificeCache.clear();
    sacrificeCache.set(key, result);
    return result;
}
function isForcingCapture(actions, i) {
    if (actions[i] == "forcing") {
        return isForcingCapture(actions, i + 2);
    }
    else if (actions[i] == "capture") {
        return true;
    }
    else {
        return false;
    }
}
function getFirstActionIndex(actionTypes) {
    for (let i = 0; i < actionTypes.length; i++) {
        if (actionTypes[i] == "capture") {
            return i;
        }
        if (actionTypes[i] === "forcing" && actionTypes[i + 1] === "capture") {
            return i;
        }
    }
    return actionTypes.length - 1;
}
export function getFirstCaptureIndexFast(fen, pvMoves) {
    const chess = new Chess(fen);
    for (let i = 0; i < pvMoves.length; i++) {
        const move = pvMoves[i];
        const moveObj = uci2move(move);
        const to = moveObj.to;
        const piece = chess.get(to);
        if (piece) {
            return i;
        }
        chess.move(moveObj);
    }
    return pvMoves.length;
}
function isNoneOrNonForcingCapture(actionTypes, i) {
    return actionTypes[i] == "none" || !isForcingCapture(actionTypes, i);
}
function getLastActionIndex(actionTypes, startIndex) {
    for (let i = startIndex; i < actionTypes.length; i++) {
        if (isNoneOrNonForcingCapture(actionTypes, i) &&
            isNoneOrNonForcingCapture(actionTypes, i + 1) &&
            isNoneOrNonForcingCapture(actionTypes, i + 2)) {
            return i;
        }
    }
    return actionTypes.length - 1;
}
function createMoveSequenceResult(chess, pvMoves) {
    const color = chess.turn();
    const opponentColor = swapColor(color);
    let gain = 0;
    let moveSequence = [];
    let pieceWin = null;
    let pieceWinSquare = null;
    let maxGain = 0;
    let lastMoveWasCapture = false;
    let lastSacrifice = null;
    let lastLeaveUndefended = null;
    const pvMoveObjs = pvMoves.map(uci2move);
    for (let i = 0; i < pvMoves.length; i++) {
        const move = pvMoves[i];
        const moveObj = pvMoveObjs[i];
        let sacrifice = null;
        let leaveUndefended = null;
        if (i % 2 == 1 && !lastMoveWasCapture) {
            const fenBefore = chess.fen();
            sacrifice = cachedDetectSacrifice(fenBefore, move);
        }
        lastMoveWasCapture = false;
        const moveResult = chess.move(moveObj);
        moveSequence.push(moveResult);
        const turn = chess.turn();
        if (sacrifice) {
            lastSacrifice = true;
            sacrifice = null;
            continue;
        }
        if (leaveUndefended) {
            lastLeaveUndefended = true;
            leaveUndefended = null;
            continue;
        }
        if (lastSacrifice) {
            lastSacrifice = false;
            continue;
        }
        if (lastLeaveUndefended) {
            lastLeaveUndefended = false;
            continue;
        }
        if (moveResult.promotion) {
            const promotion = moveResult.promotion;
            const value = pieceValues[promotion];
            if (turn == opponentColor) {
                gain += value - 1;
            }
            else {
                gain -= value - 1;
            }
        }
        if (moveResult?.captured) {
            const captured = moveResult.captured;
            lastMoveWasCapture = true;
            if (turn == opponentColor) {
                const value = pieceValues[captured];
                if (value >= maxGain) {
                    maxGain = value;
                    pieceWin = captured;
                    pieceWinSquare = moveResult.to;
                }
                gain += value;
            }
            else {
                gain -= pieceValues[captured];
            }
        }
    }
    return {
        gain,
        moveSequence,
        pieceWin,
        pieceWinSquare,
    };
}
const EVAL_RESULT_CACHE_CAP = 2000;
const evalResultCache = new Map();
export function getEvalResult(fen, pvMoves, isBlunder) {
    const pvMovesInput = pvMoves.slice();
    const cacheKey = `${fen}|${isBlunder ? 1 : 0}|${pvMovesInput.join(" ")}`;
    const cached = evalResultCache.get(cacheKey);
    if (cached)
        return cached;
    const captureThreshold = isBlunder ? 4 : 3;
    const firstCaptureIndex = getFirstCaptureIndexFast(fen, pvMovesInput);
    if (firstCaptureIndex >= captureThreshold) {
        const result = {
            gain: 0,
            moveSequence: [],
            pieceWin: null,
            pieceWinSquare: null,
            pvMoves: [],
            actionTypes: [],
        };
        if (evalResultCache.size > EVAL_RESULT_CACHE_CAP)
            evalResultCache.clear();
        evalResultCache.set(cacheKey, result);
        return result;
    }
    const actionTypesResult = getActionTypes(fen, pvMovesInput);
    const actionTypes = actionTypesResult.actions;
    pvMoves = actionTypesResult.pvMoves;
    const firstActionIndex = getFirstActionIndex(actionTypes);
    const threshold = isBlunder ? 4 : 3;
    if (firstActionIndex >= threshold) {
        const result = {
            gain: 0,
            moveSequence: [],
            pieceWin: null,
            pieceWinSquare: null,
            pvMoves: [],
            actionTypes: [],
        };
        if (evalResultCache.size > EVAL_RESULT_CACHE_CAP)
            evalResultCache.clear();
        evalResultCache.set(cacheKey, result);
        return result;
    }
    const lastActionIndex = getLastActionIndex(actionTypes, threshold);
    pvMoves = pvMoves.slice(0, lastActionIndex + 1);
    const chess = new Chess(fen);
    const opponentColor = chess.turn();
    let { gain, moveSequence, pieceWin, pieceWinSquare } = createMoveSequenceResult(chess, pvMoves);
    const lastMove = moveSequence[moveSequence.length - 1];
    if (lastMove?.captured) {
        const evalResult = exchangeEvaluation(chess.fen(), lastMove.to, {
            from: lastMove.from,
            backtrackBadMoves: true,
            filteredByLoss: true,
        });
        if (chess.turn() == opponentColor) {
            gain += evalResult.gain;
        }
        else {
            gain -= evalResult.gain;
        }
    }
    const result = {
        gain,
        moveSequence,
        pieceWin,
        pieceWinSquare,
        actionTypes,
        pvMoves,
    };
    if (evalResultCache.size > EVAL_RESULT_CACHE_CAP)
        evalResultCache.clear();
    evalResultCache.set(cacheKey, result);
    return result;
}
export function detectNegativeStockfishMaterialWin(position, lastPosition, negativeMaterialWin) {
    const isBlunder = position.moveType == MoveType.BLUNDER ||
        position.moveType == MoveType.MISTAKE;
    const gainMargin = isBlunder ? 0 : 1;
    const userPvMoves = [position.move, ...position.pvMoves];
    const bestPvMoves = lastPosition.pvMoves;
    const user = getEvalResult(lastPosition.fen, userPvMoves, isBlunder);
    const best = getEvalResult(lastPosition.fen, bestPvMoves, isBlunder);
    const bothLosesGain = user.gain < 0 && best.gain < 0;
    let isMiss = best.gain > 0 && best.gain > user.gain + gainMargin && !bothLosesGain;
    let isAllow = user.gain < 0;
    if (isMiss && isAllow) {
        if (Math.abs(best.gain) >= Math.abs(user.gain)) {
            isMiss = true;
            isAllow = false;
        }
        else {
            isMiss = false;
            isAllow = true;
        }
    }
    if ((isMiss || isAllow) && negativeMaterialWin) {
        if (negativeMaterialWin.termAction == TermAction.BETTER_WAY ||
            negativeMaterialWin.termAction == TermAction.MISS) {
            let follows = true;
            let i = 0;
            negativeMaterialWin.evalResult.moveSequence.forEach((move) => {
                const pvMove = best.pvMoves[i];
                if (!moveEquals(move, uci2move(pvMove))) {
                    follows = false;
                }
                i++;
            });
            const nextActionMove = best.actionTypes[i + 1];
            if (follows && (nextActionMove == "none" || nextActionMove == null)) {
                return null;
            }
        }
        if (negativeMaterialWin.termAction == TermAction.ALLOW) {
            let follows = true;
            let i = 1;
            negativeMaterialWin.evalResult.moveSequence.forEach((move) => {
                const pvMove = user.pvMoves[i];
                if (!moveEquals(move, uci2move(pvMove))) {
                    follows = false;
                }
                i++;
            });
            const nextActionMove = user.actionTypes[i + 1];
            if (follows && (nextActionMove == "none" || nextActionMove == null)) {
                return null;
            }
        }
    }
    if (isMiss) {
        const termAction = negativeMaterialWin?.termAction == TermAction.BETTER_WAY
            ? TermAction.BETTER_WAY
            : TermAction.MISS;
        return {
            term: ChessTermType.TACTIC,
            termAction,
            evalResult: best,
            pvMoves: best.pvMoves,
            maxGain: best.gain,
        };
    }
    if (isAllow) {
        return {
            term: ChessTermType.TACTIC,
            termAction: TermAction.ALLOW,
            evalResult: user,
            pvMoves: user.pvMoves.slice(1, user.pvMoves.length - 1),
            maxGain: Math.abs(user.gain),
        };
    }
}
export function detectPositiveStockfishMaterialWin(position, lastPosition) {
    const chess = new Chess(lastPosition.fen);
    if (chess.inCheck()) {
        return null;
    }
    const userPvMoves = [position.move, ...position.pvMoves];
    const user = getEvalResult(lastPosition.fen, userPvMoves, false);
    if (user.gain > 1) {
        const userPvMovesBefore = lastPosition.pvMoves;
        const userBeforeMove = getEvalResult(lastPosition.fen, userPvMovesBefore, false);
        if (userBeforeMove.gain > 1) {
            if (userBeforeMove.pieceWinSquare == user.pieceWinSquare) {
                return null;
            }
        }
        return {
            term: ChessTermType.TACTIC,
            termAction: TermAction.PERFORM,
            evalResult: user,
            pvMoves: user.pvMoves.slice(1, user.pvMoves.length - 1),
            maxGain: user.gain,
        };
    }
    return null;
}
//# sourceMappingURL=stockfishMaterialWin.js.map